import { motion } from 'framer-motion'
import { Mail, Phone, Clock, Send, CheckCircle, AlertCircle, Instagram, Facebook, Linkedin, Globe } from 'lucide-react'
import { useState } from 'react'
import { useLanguage } from '../context/LanguageContext'
import emailjs from '@emailjs/browser'
import SEO from '../components/SEO'
import { db } from '../firebase'
import { collection, addDoc, serverTimestamp } from 'firebase/firestore'

const Contact = () => {
  const { t } = useLanguage()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' | 'error' | null

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      console.log('Sending email...')
      
      // Save to Firebase first
      await addDoc(collection(db, 'contactSubmissions'), {
        name: formData.name,
        email: formData.email,
        message: formData.message,
        createdAt: serverTimestamp(),
        read: false
      })
      
      // EmailJS configuration
      const result = await emailjs.send(
        'service_tn8xlnj',
        'template_anw5r65',
        {
          name: formData.name,
          user_email: formData.email,
          message: formData.message
        },
        'Ac73yOgPaJV0tu4Ri'
      )
      
      console.log('EmailJS Success:', result)
      setSubmitStatus('success')
      setFormData({ name: '', email: '', message: '' })
    } catch (error) {
      console.error('Error:', error)
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
      setTimeout(() => setSubmitStatus(null), 5000)
    }
  }

  return (
    <>
      <SEO 
        title={t('seo.contact.title')}
        description={t('seo.contact.description')}
        keywords={t('seo.contact.keywords')}
      />
      <div className="mt-20">
      {/* Hero */}
      <section className="py-24 bg-gradient-to-br from-dark via-bronze-950 to-dark">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="section-title mb-6">
              {t('contact.title')} <span className="text-gradient-gold">{t('contact.titleHighlight')}</span>
            </h1>
            <p className="text-xl text-beige-200 max-w-3xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-gradient-to-br from-bronze-950 to-dark">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="card-premium">
                <h2 className="text-3xl font-display font-bold text-cream mb-6">
                  {t('contact.send')}
                </h2>
                
                {/* Success Message */}
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-green-500/20 border border-green-500 rounded-lg flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    <p className="text-green-400">Message sent successfully! We'll get back to you soon.</p>
                  </motion.div>
                )}

                {/* Error Message */}
                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg flex items-center gap-3"
                  >
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <p className="text-red-400">Failed to send message. Please try again or email us directly.</p>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-cream font-semibold mb-2">{t('contact.name')}</label>
                    <input
                      type="text"
                      name="user_name"
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-cream font-semibold mb-2">{t('contact.email')}</label>
                    <input
                      type="email"
                      name="user_email"
                      required
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-cream font-semibold mb-2">{t('contact.message')}</label>
                    <textarea
                      name="message"
                      required
                      rows={6}
                      disabled={isSubmitting}
                      className="w-full px-4 py-3 rounded-lg border-2 border-gold-500/20 bg-dark/50 text-cream focus:border-gold-500 focus:outline-none transition-colors resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    />
                  </div>
                  <button 
                    type="submit" 
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span>{isSubmitting ? t('common.loading') : t('contact.send')}</span>
                    <Send className={`w-5 h-5 ${isSubmitting ? 'animate-pulse' : ''}`} />
                  </button>
                </form>
              </div>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl font-display font-bold text-cream mb-6">
                  Contact Information
                </h2>
                <p className="text-beige-200 leading-relaxed mb-8">
                  Na jemi agjenci 100% online - punojmë me klientë nga e gjithë bota. Kontaktoni në çdo kohë!
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-gold flex items-center justify-center flex-shrink-0 shadow-gold">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-cream mb-1">Email</h3>
                    <a href="mailto:info@contenta.marketing" className="text-gold-400 hover:text-gold-300 transition-colors">
                      info@contenta.marketing
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-bronze flex items-center justify-center flex-shrink-0 shadow-bronze">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-cream mb-1">WhatsApp</h3>
                    <a href="tel:+355695658665" className="text-gold-400 hover:text-gold-300 transition-colors">
                      +355 69 565 8665
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-gold flex items-center justify-center flex-shrink-0 shadow-gold">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-cream mb-1">Availability</h3>
                    <p className="text-beige-200">
                      24/7 Online Support<br />
                      <span className="text-gold-400">Përgjigje brenda 2 orësh</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media & Online Presence Card */}
              <div className="card-premium">
                <h3 className="text-xl font-display font-bold text-cream mb-6 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-gold-400" />
                  Na Gjeni Online
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <a
                    href="https://instagram.com/contenta.al"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-dark/50 rounded-xl border border-gold-400/20 hover:border-gold-400/50 hover:bg-dark/70 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Instagram className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-cream text-sm">Instagram</p>
                      <p className="text-xs text-beige-200">@contenta.al</p>
                    </div>
                  </a>

                  <a
                    href="https://facebook.com/contenta.al"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-dark/50 rounded-xl border border-gold-400/20 hover:border-gold-400/50 hover:bg-dark/70 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Facebook className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-cream text-sm">Facebook</p>
                      <p className="text-xs text-beige-200">@contenta.al</p>
                    </div>
                  </a>

                  <a
                    href="https://linkedin.com/company/contenta-marketing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-dark/50 rounded-xl border border-gold-400/20 hover:border-gold-400/50 hover:bg-dark/70 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-700 to-blue-800 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Linkedin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-cream text-sm">LinkedIn</p>
                      <p className="text-xs text-beige-200">Contenta Marketing</p>
                    </div>
                  </a>

                  <a
                    href="https://wa.me/355695658665"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-4 bg-dark/50 rounded-xl border border-gold-400/20 hover:border-gold-400/50 hover:bg-dark/70 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-cream text-sm">WhatsApp</p>
                      <p className="text-xs text-beige-200">Chat Now</p>
                    </div>
                  </a>
                </div>

                {/* Online Badge */}
                <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <div className="absolute inset-0 w-3 h-3 bg-green-400 rounded-full animate-ping"></div>
                    </div>
                    <div>
                      <p className="text-green-400 font-semibold text-sm">Online Now</p>
                      <p className="text-beige-200 text-xs">Ready to help you grow your business</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
    </>
  )
}

export default Contact
